import { Actor, CollisionType, Color, Circle, Engine, vec, Shape, Animation } from "excalibur";
import { PROGRESSION, VISUALS, VIRUS } from "../config";
import { Macrophage } from "./Macrophage";
import { ItemCollision } from "../systems/CollisionGroups";
import { Resources } from "../resources";
import { createPackedAnimation } from "../utils/AtlasLoader";

/**
 * Shared animation cache for all Antigen instances
 * This prevents creating new Animation objects for every antigen spawned
 */
let sharedAntigenAnimation: Animation | null = null;
let sharedFallbackGraphic: Circle | null = null;

function getSharedAntigenAnimation(): Animation | null {
    if (sharedAntigenAnimation) return sharedAntigenAnimation;

    try {
        sharedAntigenAnimation = createPackedAnimation(
            Resources.AntigenImage,
            Resources.AntigenJson,
            10 // 10 FPS
        );

        // Scale animation to fit collectible size (1.5x size increase)
        const targetSize = 16 * 1.5;
        if (sharedAntigenAnimation.width > 0) {
            const scale = targetSize / sharedAntigenAnimation.width;
            sharedAntigenAnimation.scale = vec(scale, scale);
        }

        return sharedAntigenAnimation;
    } catch (error) {
        console.warn('Failed to load antigen animation', error);
        return null;
    }
}

function getSharedFallbackGraphic(): Circle {
    if (!sharedFallbackGraphic) {
        sharedFallbackGraphic = new Circle({
            radius: 5,
            color: Color.fromHex(VISUALS.COLORS.ANTIGEN_XP),
            strokeColor: Color.fromHex('#fef3c7'),
            lineWidth: 2,
        });
    }
    return sharedFallbackGraphic;
}

/**
 * Antigen - XP Gem / Collectible
 * 
 * Dropped by viruses when killed. Collecting these triggers "Clonal Expansion" (leveling up).
 * Represents viral RNA/antigens being processed by the immune system.
 * 
 * Uses Passive collision so player walks over to collect without bumping.
 */
export class Antigen extends Actor {
    private xpValue: number;
    private target: Macrophage | null = null;
    private attractSpeed: number = 300;
    private isBeingAttracted: boolean = false;
    private lifetime: number = 10000;

    constructor(x: number, y: number, xpValue: number = VIRUS.XP_VALUE) {
        super({
            name: 'Antigen',
            pos: vec(x, y),
            radius: 4,
            color: Color.fromHex(VISUALS.COLORS.ANTIGEN_XP),
            collisionType: CollisionType.Passive,
            collisionGroup: ItemCollision, // Only collides with player
        });
        this.xpValue = xpValue;
    }

    override onInitialize(_engine: Engine): void {
        // Set up circular collider and restore collision group manually
        const collider = Shape.Circle(6) as any;
        collider.group = ItemCollision;
        this.collider.set(collider);

        // Use shared animation (avoids creating new Animation objects for each antigen)
        const animation = getSharedAntigenAnimation();
        if (animation) {
            this.graphics.use(animation);
        } else {
            this.graphics.use(getSharedFallbackGraphic());
        }

        // Auto-destroy after lifetime
        this.actions.delay(this.lifetime).callMethod(() => {
            this.kill();
        });
    }



    override onPreUpdate(_engine: Engine, delta: number): void {
        this.lifetime -= delta;

        // Check if player is in range for attraction (Chemotaxis)
        if (this.target) {
            const distance = this.target.pos.distance(this.pos);

            if (distance < PROGRESSION.PICKUP_RADIUS) {
                this.target.collectXP(this.xpValue);
                this.kill();
                return;
            }

            if (this.isBeingAttracted || distance < PROGRESSION.MAGNET_RADIUS) {
                this.isBeingAttracted = true;
                const direction = this.target.pos.sub(this.pos).normalize();
                this.vel = direction.scale(this.attractSpeed);
            }
        }
    }

    /**
     * Set the target for this antigen to be attracted to
     */
    public setTarget(target: Macrophage): void {
        this.target = target;
    }
}
